import { Button, ModalProps, Row, Space, notification } from "antd";
import { CreateSimpleUserCommand, UserDetailVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import CustomModal from "components/ui/CustomModal";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useGrapeAntdForm } from "widgets/form";
import CustomForm from "components/form/CustomForm";
import UserFormItems from "./UserFormItems";
import useEditFormModal from "hooks/useEditModal";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";
import { MaterialIcon } from "components/MaterialIcon";
import { mdiEmail, mdiPencil } from "@mdi/js";
import styles from "./UserCreateModal.module.scss";

const UserCreateModal: FC<ModalProps> = ({ visible, onCancel }) => {
  const { t } = useTranslation();
  const formUtils = useGrapeAntdForm<CreateSimpleUserCommand>();
  const dispatch = useDispatch();
  const { users, editingUserId } = useSelector(
    (state) => state.usersListSettings
  );
  const { createUser, editUser, inviteUser } = useSelector(
    (state) => state.loading.effects.usersListSettings
  );

  const getInitialValues = useCallback(
    (itemData: UserDetailVm) => ({
      familyname: itemData.familyname,
      middlename: itemData.middlename,
      forename: itemData.forename,
      username: itemData.username,
      orgName: itemData.orgName,
      isRegistered: itemData.isRegistered,
    }),
    []
  );

  const { formMode, setFormMode } = useEditFormModal<any, any>({
    formUtils,
    getInitialValues,
    modalIsVisible: visible,
    resetItemId: dispatch.usersListSettings.resetEditingUserData,
    itemId: editingUserId,
    list: users,
  });

  const handleFinish = useCallback(
    async (values: CreateSimpleUserCommand) => {
      if (formMode === "Create") {
        await dispatch.usersListSettings.createUser(values);
      }

      if (formMode === "Edit") {
        await dispatch.usersListSettings.editUser({
          ...values,
          id: editingUserId,
        });
      }

      onCancel?.("" as any);
      formUtils.form.resetFields();
    },
    [
      dispatch.usersListSettings,
      formUtils.form,
      onCancel,
      editingUserId,
      formMode,
    ]
  );

  const handleInviteButtonClick = useCallback(
    async (id?: string) => {
      try {
        if (id) {
          await dispatch.usersListSettings.inviteUser(id);
          notification.success({
            message: t("settings.users.inviteUserSuccess"),
          });
        }
      } catch (error) {
        // Note: error handled globally in errorHandlingMiddleware
      }
    },
    [dispatch.usersListSettings, t]
  );

  return (
    <CustomModal
      visible={visible}
      onCancel={onCancel}
      title={
        formMode === "View"
          ? t("settings.user.modalTitle")
          : formMode === "Edit"
          ? t("settings.users.editModalTitle")
          : t("settings.users.createModalTitle")
      }
      size="large"
      onOk={formUtils.form.submit}
      destroyOnClose
      okButtonProps={{
        loading: createUser.loading || editUser.loading,
        disabled: formMode === "View",
      }}
      cancelButtonProps={{ disabled: createUser.loading || editUser.loading }}
      formInstance={formUtils.form}
    >
      <CustomForm
        formUtils={formUtils}
        onFinish={handleFinish}
        viewMode={formMode === "View"}
        className={styles.form}
      >
        <Row justify="end">
          <Space>
            {formMode !== "Create" && (
              <Button
                icon={<MaterialIcon path={mdiEmail} />}
                onClick={() => handleInviteButtonClick(editingUserId)}
                type="primary"
                loading={inviteUser.loading}
              >
                {t("common.invite")}
              </Button>
            )}

            {formMode === "View" && (
              <Permission acceptableClaims={[Claims.User.Write]}>
                <Button
                  icon={<MaterialIcon path={mdiPencil} />}
                  onClick={() => setFormMode("Edit")}
                >
                  {t("common.edit")}
                </Button>
              </Permission>
            )}
          </Space>
        </Row>

        <UserFormItems formUtils={formUtils} formMode={formMode} />
      </CustomForm>
    </CustomModal>
  );
};

export default UserCreateModal;
